import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import documentoService from '../services/documentoService';
import ConfirmModal from '../components/ConfirmModal';
import '../styles/AnulacionDocumentos.css';
import { LuFileDigit, LuSearch, LuTrash2 } from 'react-icons/lu';

const AnulacionDocumentos = () => {
  const { usuario } = useAuth();
  const toast = useToast();

  const [idDocumento, setIdDocumento] = useState('');
  const [documento, setDocumento] = useState(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmAnular, setConfirmAnular] = useState(false);

  const handleBuscar = async () => {
    if (!idDocumento) {
      toast.warning('Ingrese el ID del documento');
      return;
    }

    setLoading(true);
    setDocumento(null);
    setMotivoAnulacion('');

    try {
      const response = await documentoService.obtenerDocumentoPorId(idDocumento);

      if (response.success) {
        const doc = response.data;
        setDocumento(doc);
        if (doc.Estado === 'AN') {
          toast.info('Este documento ya está anulado');
        }
      }
    } catch (error) {
      if (error.message && error.message.includes('no encontrado')) {
        toast.error('Documento no encontrado');
      } else {
        toast.error(error.message || 'Error al buscar documento');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLimpiar = () => {
    setIdDocumento('');
    setDocumento(null);
    setMotivoAnulacion('');
  };

  const handleAnular = () => {
    if (!motivoAnulacion.trim()) {
      toast.warning('Debe ingresar un motivo de anulación');
      return;
    }
    setConfirmAnular(true);
  };

  const doAnular = async () => {
    setConfirmAnular(false);
    setLoading(true);

    try {
      const response = await documentoService.anularDocumento(
        documento.id,
        motivoAnulacion
      );

      if (response.success) {
        toast.success('Documento anulado exitosamente');
        setDocumento({
          ...documento,
          Estado: 'AN',
          MotivoAnulacion: motivoAnulacion,
          estadoInfo: {
            ...documento.estadoInfo,
            Nombre: 'Anulado',
            CodEstado: 'AN',
          },
        });
        setMotivoAnulacion('');
      }
    } catch (error) {
      toast.error(error.message || 'Error al anular documento');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleBuscar();
  };

  const formatMonto = (monto) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(monto);

  const formatFecha = (fecha) =>
    new Date(fecha + 'T00:00:00').toLocaleDateString('es-CL');

  return (
    <div className="anulacion-container">
      <div className="anulacion-header">
        <h2>Anulación de documentos</h2>
      </div>

      {/* Búsqueda */}
      <div className="busqueda-card">
        <div className="form-group">
          <label htmlFor="idDocumento" className="form-label">
            <i className="icon"><LuFileDigit /></i> ID del Documento
          </label>
          <input
            type="number"
            id="idDocumento"
            value={idDocumento}
            onChange={(e) => setIdDocumento(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ingrese el ID del documento"
            disabled={loading}
            autoFocus
          />
        </div>

        <div className="busqueda-buttons">
          <button
            className="btn-buscar"
            onClick={handleBuscar}
            disabled={loading || !idDocumento}
          >
            <i className="icon"><LuSearch /></i>
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
          <button
            className="btn-limpiar"
            onClick={handleLimpiar}
            disabled={loading}
          >
            <i className="icon"><LuTrash2 /></i>
            Limpiar
          </button>
        </div>
      </div>

      {/* Información del documento */}
      {documento && (
        <div className="documento-card">
          <div className="documento-info">
            <div className="info-row">
              <div className="info-item">
                <span className="info-label">ID</span>
                <span className="info-value">{documento.id}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Num Doc.</span>
                <span className="info-value">{documento.NumDoc}</span>
              </div>
            </div>

            <div className="info-row">
              <div className="info-item">
                <span className="info-label">Tipo Doc.</span>
                <span className="info-value">
                  {documento.tipoDocInfo?.Nombre || 'N/A'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Monto</span>
                <span className={`info-value ${documento.TipoRegistro === 'Ingreso' ? 'monto-ingreso' : 'monto-gasto'}`}>
                  {formatMonto(documento.Monto)}
                </span>
              </div>
            </div>

            <div className="info-row">
              <div className="info-item">
                <span className="info-label">Tipo</span>
                <span className="info-value">{documento.TipoRegistro}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Grupo</span>
                <span className="info-value">
                  {documento.grupoInfo?.Descripcion || documento.grupoInfo?.Nombre || 'N/A'}
                </span>
              </div>
            </div>

            <div className="info-row">
              <div className="info-item">
                <span className="info-label">Fecha</span>
                <span className="info-value">{formatFecha(documento.Fecha)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Estado</span>
                <span className={`badge-estado ${documento.Estado === 'AC' ? 'activo' : 'anulado'}`}>
                  {documento.estadoInfo?.Nombre || documento.Estado}
                </span>
              </div>
            </div>

            <div className="info-row">
              <div className="info-item full-width">
                <span className="info-label">Usuario</span>
                <span className="info-value">
                  {documento.usuarioInfo?.NombreUsu || 'N/A'}
                </span>
              </div>
            </div>

            {documento.Glosa && (
              <div className="info-row">
                <div className="info-item full-width">
                  <span className="info-label">Glosa</span>
                  <span className="info-value">{documento.Glosa}</span>
                </div>
              </div>
            )}

            {documento.Estado === 'AN' && documento.MotivoAnulacion && (
              <div className="info-row">
                <div className="info-item full-width anulado-info">
                  <span className="info-label">Motivo de Anulación</span>
                  <span className="info-value">{documento.MotivoAnulacion}</span>
                </div>
              </div>
            )}
          </div>

          {/* Formulario de anulación */}
          {documento.Estado === 'AC' && (
            <div className="anulacion-form">
              <div className="form-group">
                <label htmlFor="motivoAnulacion" className="form-label">
                  <i className="icon">💬</i> Motivo de anulación
                </label>
                <textarea
                  id="motivoAnulacion"
                  value={motivoAnulacion}
                  onChange={(e) => setMotivoAnulacion(e.target.value)}
                  placeholder="Ingrese el motivo de anulación"
                  rows="4"
                  disabled={loading}
                  maxLength="500"
                />
                <small className="char-count">
                  {motivoAnulacion.length}/500 caracteres
                </small>
              </div>

              <button
                className="btn-anular"
                onClick={handleAnular}
                disabled={loading || !motivoAnulacion.trim()}
              >
                <i className="icon">❌</i>
                {loading ? 'Anulando...' : 'Anular'}
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmAnular}
        title="Anular Documento"
        message="¿Está seguro que desea anular este documento? Esta acción no se puede deshacer."
        confirmLabel="Anular"
        onConfirm={doAnular}
        onCancel={() => setConfirmAnular(false)}
        danger
      />
    </div>
  );
};

export default AnulacionDocumentos;
